import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import VehicleDetailModal from "@/components/VehicleDetailModal";

const empty = {
  license_plate: "",
  vehicle_type: "",
  seat_capacity: 0,
  wheelchair_capacity: 0,
  active: true,
};

export default function Vehicles() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try { setList((await api.get("/vehicles")).data); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (v) => { setEditing(v); setForm({ ...empty, ...v }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, seat_capacity: Number(form.seat_capacity) || 0, wheelchair_capacity: Number(form.wheelchair_capacity) || 0 };
      if (editing) {
        await api.patch(`/vehicles/${editing.id}`, payload);
        toast.success("Fahrzeug aktualisiert");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Fahrzeug angelegt");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (v) => {
    try { await api.patch(`/vehicles/${v.id}`, { active: !v.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = list.filter((v) => {
    const q = search.toLowerCase();
    return !q || `${v.license_plate} ${v.vehicle_type}`.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl heading font-semibold">Fahrzeuge</h1>
          <p className="text-sm text-muted-foreground">Fahrzeuge anlegen und verwalten.</p>
        </div>
        <Button onClick={startCreate} data-testid="vehicles-create-button">
          <Plus className="h-4 w-4 mr-1" /> Neues Fahrzeug
        </Button>
      </div>

      <Card className="p-3">
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suche…" className="pl-9" data-testid="vehicles-search-input" />
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2">Kennzeichen</th>
              <th className="text-left px-4 py-2">Typ</th>
              <th className="text-left px-4 py-2">Sitze</th>
              <th className="text-left px-4 py-2">Rollstuhl</th>
              <th className="text-left px-4 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.id} className="border-t border-border row-hover">
                <td className="px-4 py-2 font-mono">
                  <button onClick={() => setDetail(v)} className="text-blue-700 dark:text-blue-300 hover:underline" data-testid={`vehicle-plate-link-${v.id}`}>
                    {v.license_plate}
                  </button>
                </td>
                <td className="px-4 py-2">{v.vehicle_type}</td>
                <td className="px-4 py-2 tabular-nums">{v.seat_capacity}</td>
                <td className="px-4 py-2 tabular-nums">{v.wheelchair_capacity}</td>
                <td className="px-4 py-2">
                  <Switch checked={v.active} onCheckedChange={() => toggleActive(v)} data-testid={`vehicle-active-toggle-${v.id}`} />
                </td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(v)} data-testid={`vehicle-edit-${v.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground text-sm">Keine Fahrzeuge gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</DialogTitle><DialogDescription>Fahrzeugdaten erfassen.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kennzeichen</Label>
              <Input value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} data-testid="vehicle-plate-input" />
            </div>
            <div>
              <Label>Fahrzeugtyp</Label>
              <Input value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} placeholder="z.B. Mercedes Sprinter" data-testid="vehicle-type-input" />
            </div>
            <div>
              <Label>Sitzplätze</Label>
              <Input type="number" min={0} value={form.seat_capacity} onChange={(e) => setForm({ ...form, seat_capacity: e.target.value })} data-testid="vehicle-seats-input" />
            </div>
            <div>
              <Label>Rollstuhlplätze</Label>
              <Input type="number" min={0} value={form.wheelchair_capacity} onChange={(e) => setForm({ ...form, wheelchair_capacity: e.target.value })} data-testid="vehicle-wheelchair-input" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-sm">Aktiv</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} data-testid="vehicle-save-button">{editing ? "Speichern" : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VehicleDetailModal vehicle={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
    </div>
  );
}
